/**
 * Studio V2's URL contract, bound to the router — the V2 twin of v1's
 * `useFlowUrl.ts`, over `studioV2Url.ts`'s own param space (never v1's).
 *
 * One write mode, unlike v1's go/patch split: V2 has no history-worthy
 * navigation of its own yet (no step, no guided hop) — every write here is an
 * in-place adjustment (the active Verordnung, the addressed statement, zoom,
 * the mobile pane), so all of them replace rather than push. A future
 * increment that adds a real navigation (e.g. leaving the workspace back into
 * the addressed statement's issue) can add its own `go` then; there is
 * nothing to push today.
 */
import { useCallback, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  mergeStudioV2Search,
  parseStudioV2Url,
  type StudioV2UrlState,
} from "./studioV2Url"

export interface StudioV2Url {
  state: StudioV2UrlState
  /** Adjust the current state in place (no history entry). */
  patch: (part: Partial<StudioV2UrlState>) => void
}

export function useStudioV2Url(): StudioV2Url {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const state = useMemo(() => parseStudioV2Url(params), [params])
  const patch = useCallback(
    (part: Partial<StudioV2UrlState>) =>
      navigate(
        {
          // `mergeStudioV2Search`, not `studioV2Search`/a hand-built
          // `?${...}`: this contract's own four keys still omit their
          // defaults (Copilot review, PR #2001), but any FOREIGN param
          // already on the URL — the overview's list filter/sort keys
          // (TUR-1211) — must survive the patch too, since this is the
          // route's only URL write.
          search: mergeStudioV2Search(params, { ...state, ...part }),
        },
        { replace: true },
      ),
    [navigate, params, state],
  )
  return { state, patch }
}
